import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
// fixed by adding lazy import in useWalletOnboardingModal file
// ESLint can't detect lazy imports in static analysis
// eslint-disable-next-line import/no-cycle
import useFeatures from 'hooks/useFeatures';
import {
  useGetActivationStatus,
  useUpdateActivationStatus,
} from 'hooks/Bank/useActivationStatus';
import useHasAccountableBanking from 'hooks/Bank/useHasAccountableBanking';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useCreateBankConnection from 'hooks/Bank/useCreateBankConnection';
import {
  useGetOnboardingStatus,
  useUpdateOnboardingStatus,
} from 'hooks/Bank/useOnboardingStatus';
import useGetCardsList from 'hooks/Bank/useGetCardsList';
import CustomLoader from 'components/molecules/CustomLoader.component';
import { fadeTransition } from 'utils/constants';
import { logAmplitude } from 'utils/amplitude';
import { AvailableCountries } from 'types/users.types';
import type {
  BankingSignupAccountDetails,
  BirthInfo,
} from 'components/pages/Banks/BankingSignUp/Onboarding/constants/onboarding.types';
import {
  getAllSubsteps,
  getMainStepForSubstep,
  type StepRenderContext,
  StepMode,
} from '../utils/stepDefinitions';
import { PostAccountStepId } from '../ActivationFlow/constants/activationStepIds';
import { STEP_COMPONENTS } from '../utils/stepComponents';
import { determineStepProgression } from '../utils/stepProgressionLogic';
import { computeInitialStep } from '../utils/stepInitializationLogic';
import { useActivationProgress } from '../ActivationFlow/hooks/useActivationProgress';
import ActivationFlowErrorBoundary from './ActivationFlowErrorBoundary';
import { bankingSignupLogger } from '../utils/bankingSignupLogger';
import type { CountryAlpha3Code } from 'types/countries';

export interface UnifiedBankingSignupFlowProps {
  onComplete?: () => void;
  initialScreenIndex?: number;
  /** Step ID to resume from (e.g., 'card_design') - takes precedence over initialScreenIndex */
  initialStepId?: string;
}

/**
 * Unified Accountable Banking Signup Flow
 * Component onboards all users:
 * 1. pre-account (sign-up) steps;
 * 2. post-account (activation) steps.
 *
 * - Pre-account steps integrate with onboarding backend API (GET/PATCH /v2/accountable-banking/onboarding)
 *   to track furthestStepCompleted and allow resuming from where the user left off.
 *
 * - Post-account steps integrate with activation backend API (when feature flag enabled)
 * - Mode detection based on hasAccountableBanking
 */
const UnifiedBankingSignupFlow: FC<UnifiedBankingSignupFlowProps> = ({
  onComplete,
  initialScreenIndex,
  initialStepId,
}) => {
  const { t } = useTranslation();
  const hasAccountableBanking = useHasAccountableBanking();
  const { data: activationStatus, isLoading: isLoadingActivationStatus } =
    useGetActivationStatus();
  const updateStep = useUpdateActivationStatus();

  const { data: user } = useGetCurrentCustomerData();
  const { data: cards, isLoading: isLoadingCards } = useGetCardsList();

  // Feature flag for post-account flow
  const USE_ACTIVATION_FLOW_ENABLED = useFeatures(
    'banks.usePostAccountActivationFlow',
    true,
  );

  // Determine current mode
  const currentMode: StepMode = hasAccountableBanking
    ? StepMode.PostAccount
    : StepMode.PreAccount;

  // State for step management
  const [currentStepIndex, setCurrentStepIndex] = useState(
    initialScreenIndex || 0,
  );
  const [isInitialized, setIsInitialized] = useState(false);

  /**
   * Tracks steps that were reached via auto-skip from issue_card step
   * These steps will have disabled backward navigation (both back button and progress bar)
   * This is generic - any step skipped from issue_card will be tracked here
   *
   * Example use cases:
   * - Skip to your_card_ready when user already has an enabled card
   * - Skip to first_transfer when user chooses to skip card creation
   * - Any future skip scenarios from issue_card step
   */
  const [autoSkippedSteps, setAutoSkippedSteps] = useState<Set<string>>(
    new Set(),
  );

  // State for pre-account flow (WalletOnboarding state)
  const [onboardingId, setOnboardingId] = useState<string>('');
  const [accountDetails, setAccountDetails] =
    useState<BankingSignupAccountDetails>();
  const [proofOfRegistrationFiles, setProofOfRegistrationFiles] = useState<
    {
      url: string;
      payload: string;
      name?: string;
    }[]
  >([]);
  const [birthDetails, setBirthDetails] = useState<BirthInfo>();
  const [residencyDetails, setResidencyDetails] = useState<{
    street: string;
    city: string;
    state?: string;
    zip: string;
    country?: CountryAlpha3Code;
  }>();
  /** See `persistOnboardingThenContinue` in stepDefinitions.helpers (onboarding PATCH dedup). */
  const [lastPersistedPayload, setLastPersistedPayload] = useState<string>();

  const { mutate: createBankConnection, isLoading: isCreatingConnection } =
    useCreateBankConnection(
      {
        numberOfConnectors: 0,
      },
      user?.country === AvailableCountries.germany,
    );

  const { data: onboardingStatus, isLoading: isLoadingOnboardingStatus } =
    useGetOnboardingStatus();
  const onboardingAPI = useUpdateOnboardingStatus();

  // Get all substeps and filter based on mode and feature flags
  const visibleSubsteps = useMemo(() => {
    const allSubsteps = getAllSubsteps();

    // If post-account flow is disabled, only show pre-account steps
    if (!USE_ACTIVATION_FLOW_ENABLED) {
      return allSubsteps.filter((s) => s.mode === StepMode.PreAccount);
    }

    // Filter country-specific steps
    return allSubsteps.filter((substep) => {
      if (substep?.countrySpecific) {
        return substep.countrySpecific.includes(
          user?.country as AvailableCountries,
        );
      }
      return true;
    });
  }, [USE_ACTIVATION_FLOW_ENABLED, user?.country]);

  // Determine which step to show on mount (pure logic extracted for testability)
  useEffect(() => {
    if (isInitialized) return;

    const initInput = {
      currentMode,
      cards,
      isLoadingCards,
      activationFurthestStepReached:
        activationStatus?.activationFurthestStepReached ?? null,
      isLoadingActivationStatus,
      furthestStepCompleted: onboardingStatus?.furthestStepCompleted ?? null,
      isLoadingOnboardingStatus,
      isActivationFlowEnabled: USE_ACTIVATION_FLOW_ENABLED,
      visibleSubsteps,
      initialStepId,
      initialScreenIndex,
    };
    const result = computeInitialStep(initInput);

    if (result.type === 'NOT_READY') return;

    bankingSignupLogger.debug(
      `Initialization resolved: step ${result.stepIndex} (${
        visibleSubsteps[result.stepIndex]?.id
      })`,
    );

    setCurrentStepIndex(result.stepIndex);
    if (result.autoSkippedSteps.size > 0) {
      setAutoSkippedSteps(result.autoSkippedSteps);
    }
    setIsInitialized(true);
    if (onboardingStatus?.onboardingId) {
      setOnboardingId(onboardingStatus.onboardingId);
    }

    // Hydrate local form state from the API so back-navigation works
    // after a modal close/reopen cycle.
    const ob = onboardingStatus?.onboarding;
    if (ob && user) {
      const isBelgium = user.country === AvailableCountries.belgium;

      setAccountDetails({
        ...user,
        firstName: ob.personalDetails?.firstName ?? user.firstName,
        lastName: ob.personalDetails?.lastName ?? user.lastName,
        email: ob.personalDetails?.email ?? user.email,
        address: {
          ...user.address,
          ...ob.businessAddress,
        },
        ...(isBelgium ? { VATNumber: ob.taxID } : { steuernummer: ob.taxID }),
        monthlyPaymentVolume: ob.monthlyPaymentVolume,
        APC1: ob.businessActivity,
        companyName: ob.companyName ?? user.companyName ?? '',
        additionalBusinessActivityDescription:
          ob.additionalBusinessActivityDescription ?? '',
        _addressSearchCompleted: !!ob.businessAddress,
      });

      if (ob.filepaths?.length) {
        setProofOfRegistrationFiles(
          ob.filepaths.map((url) => ({ url, payload: '' })),
        );
      }

      if (ob.birth) {
        setBirthDetails(ob.birth);
      }

      if (ob.personalDetails?.address) {
        const addr = ob.personalDetails.address as {
          street: string;
          city: string;
          zip: string;
          country?: CountryAlpha3Code;
        };
        setResidencyDetails({
          street: addr.street,
          city: addr.city,
          zip: addr.zip,
          country: addr.country,
        });
      }
    }
  }, [
    currentMode,
    USE_ACTIVATION_FLOW_ENABLED,
    activationStatus,
    isLoadingActivationStatus,
    onboardingStatus,
    isLoadingOnboardingStatus,
    isInitialized,
    visibleSubsteps,
    initialScreenIndex,
    initialStepId,
    cards,
    isLoadingCards,
  ]);

  // Handle step progression — all cases (pre-account + post-account)
  // routed through determineStepProgression for centralised decision-making.
  const handleContinue = () => {
    // Prevent double-clicks while update is in progress
    if (updateStep.isLoading || onboardingAPI.isLoading) {
      return;
    }

    const action = determineStepProgression({
      currentStepIndex,
      visibleSubsteps,
      isFeatureEnabled: USE_ACTIVATION_FLOW_ENABLED,
      // todo: rename to furthestActivationStep
      furthestBackendStep: activationStatus?.activationFurthestStepReached,
      furthestOnboardingStep: onboardingStatus?.furthestStepCompleted,
    });

    switch (action.type) {
      case 'MOVE_LOCAL':
        // Simple local navigation (no backend update)
        if (action.nextIndex !== undefined) {
          setCurrentStepIndex(action.nextIndex);
        }
        break;

      case 'UPDATE_BACKEND_THEN_MOVE':
        if (action.backendStepToUpdate && action.nextIndex !== undefined) {
          updateStep.mutate(action.backendStepToUpdate, {
            onSuccess: () => setCurrentStepIndex(action.nextIndex!),
          });
        }
        break;

      case 'UPDATE_BACKEND_THEN_COMPLETE':
        // Update backend tracking, then complete flow
        if (action.backendStepToUpdate) {
          updateStep.mutate(action.backendStepToUpdate, {
            onSuccess: () => onComplete?.(),
          });
        }
        break;

      case 'UPDATE_ONBOARDING_THEN_MOVE':
        if (action.onboardingStepToUpdate && action.nextIndex !== undefined) {
          onboardingAPI.mutate(
            { furthestStepCompleted: action.onboardingStepToUpdate },
            { onSuccess: () => setCurrentStepIndex(action.nextIndex!) },
          );
        }
        break;

      case 'UPDATE_ONBOARDING_THEN_COMPLETE':
        if (action.onboardingStepToUpdate) {
          onboardingAPI.mutate(
            { furthestStepCompleted: action.onboardingStepToUpdate },
            { onSuccess: () => onComplete?.() },
          );
        }
        break;

      case 'COMPLETE':
        // Complete flow immediately (no backend update)
        onComplete?.();
        break;
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  // Handle skip to specific substep
  const handleSkip = (targetSubstepId: string) => {
    const targetIndex = visibleSubsteps.findIndex(
      (s) => s.id === targetSubstepId,
    );
    if (targetIndex !== -1) {
      const currentSubstep = visibleSubsteps[currentStepIndex];
      bankingSignupLogger.stepTransition(
        currentSubstep?.id || currentStepIndex,
        targetSubstepId,
        'user skip',
      );

      // Track auto-skipped steps (steps reached via skip from issue_card)
      // These steps should prevent backward navigation
      if (currentSubstep?.id === PostAccountStepId.IssueCard) {
        setAutoSkippedSteps((prev) => new Set(prev).add(targetSubstepId));
      }

      setCurrentStepIndex(targetIndex);
    } else {
      bankingSignupLogger.warn(`Skip target not found: ${targetSubstepId}`);
    }
  };

  // Handle navigation to a specific substep from progress bar
  // TODO: Progress bar navigation temporarily disabled - will be re-enabled with proper validation
  const handleProgressSegmentClick = (_substepId: string) => {
    return;
  };

  // Calculate progress information for the current step
  const currentSubstep = visibleSubsteps[currentStepIndex];

  // Get metadata for current step (title, subtitle, icon)
  const currentMainStep = currentSubstep
    ? getMainStepForSubstep(currentSubstep.id)
    : undefined;

  const { progressSegments, currentStepNumber, totalSteps } =
    useActivationProgress({
      visibleSubsteps,
      currentStepIndex,
      currentStepId: currentSubstep?.id || '',
      currentMainStep, // Pass current main step to filter substeps
      disableNavigation: currentSubstep?.id
        ? autoSkippedSteps.has(currentSubstep.id)
        : false,
    });

  // Prepare metadata props
  const stepTitle = currentSubstep?.titleKey
    ? t(currentSubstep.titleKey)
    : undefined;
  const stepSubtitle = currentSubstep?.subtitleKey
    ? t(currentSubstep.subtitleKey)
    : undefined;
  const stepIcon = currentSubstep?.modalIcon; // Icon is optional and substep-specific

  // Log amplitude events for substeps (only after init so we don't fire step 0 then the real step)
  useEffect(() => {
    if (!isInitialized) return;

    const substep = visibleSubsteps[currentStepIndex];
    if (!substep) return;

    if (substep.analyticsEvent) {
      logAmplitude(substep.analyticsEvent);
    }
  }, [isInitialized, currentStepIndex, visibleSubsteps]);

  // Build context for step rendering
  const stepContext: StepRenderContext = {
    // Navigation
    onContinue: handleContinue,
    onBack: handleBack,
    onSkip: handleSkip,

    // Progress information
    progressSegments,
    onProgressSegmentClick: handleProgressSegmentClick,
    currentStepNumber,
    totalSteps,

    // Step metadata (automatically injected)
    title: stepTitle,
    subtitle: stepSubtitle,
    icon: stepIcon,

    // Loading states
    isUpdatingStep: currentSubstep?.activationStep
      ? updateStep.isLoading
      : onboardingAPI.isLoading,

    // State
    onboardingId,
    setOnboardingId,
    accountDetails,
    setAccountDetails: (details: BankingSignupAccountDetails) =>
      setAccountDetails(details),
    proofOfRegistrationFiles,
    setProofOfRegistrationFiles,
    birthDetails,
    setBirthDetails,
    residencyDetails,
    setResidencyDetails,

    // API
    onboardingAPI,
    createBankConnection,
    isCreatingConnection,

    // Onboarding PATCH dedup (stepDefinitions.helpers.persistOnboardingThenContinue)
    lastPersistedPayload,
    setLastPersistedPayload,

    // User data
    user,
    t,
  };

  // Render current substep using metadata
  const renderStep = () => {
    // Safety check: ensure we have a valid substep
    if (!currentSubstep) {
      bankingSignupLogger.error('No current substep found');
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-primary-600 mb-4">
              Unable to load this step. Please try again.
            </p>
          </div>
        </div>
      );
    }

    // Resolve component from registry
    const Component = STEP_COMPONENTS[currentSubstep.componentName];

    // Safety check: ensure component exists in registry
    if (!Component) {
      bankingSignupLogger.error(
        `Component not found in registry: ${currentSubstep.componentName}`,
      );
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-primary-600 mb-4">
              Step configuration error. Please contact support.
            </p>
            <p className="text-sm text-primary-400">
              Missing component: {currentSubstep.componentName}
            </p>
          </div>
        </div>
      );
    }

    // Generate props from context
    try {
      const props = currentSubstep.renderProps(stepContext);
      return <Component {...props} />;
    } catch (error) {
      bankingSignupLogger.error('Error generating step props:', error);
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-primary-600">Failed to render step</p>
          </div>
        </div>
      );
    }
  };

  if (
    !isInitialized ||
    (currentMode === StepMode.PostAccount && isLoadingActivationStatus) ||
    (currentMode === StepMode.PreAccount && isLoadingOnboardingStatus)
  ) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <CustomLoader />
      </div>
    );
  }

  return (
    <ActivationFlowErrorBoundary>
      <div className="relative w-full">
        <AnimatePresence mode="wait">
          <motion.div {...fadeTransition} key={currentSubstep?.id}>
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>
    </ActivationFlowErrorBoundary>
  );
};

export default UnifiedBankingSignupFlow;
